import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    IconButton,
    Chip,
    Box,
    CircularProgress
} from '@mui/material';
import {
    Close as CloseIcon,
    Visibility as VisibilityIcon,
    Edit as EditIcon,
    AccountTree as AccountTreeIcon
} from '@mui/icons-material';
import { COLORS } from '../../settings/themeSettings';

/**
 * Dialog PERSONAE
 * Affiche les structures où ce client apparaît (affiliations)
 */
const PersonaeDialog = ({ open, onClose, client }) => {
    const [loading, setLoading] = useState(false);
    const [affiliations, setAffiliations] = useState([]);

    useEffect(() => {
        if (open && client) {
            loadAffiliations();
        }
    }, [open, client]);

    const loadAffiliations = async () => {
        setLoading(true);
        try {
            // TODO: Remplacer par appel API réel
            // const response = await clientService.getAffiliations(client.id);

            // Simulation de données pour démonstration
            const mockAffiliations = [
                {
                    id: 1,
                    structureParent: 'ACME Corporation',
                    role: 'Contact Principal',
                    dateAjout: '2024-01-15',
                    statut: 'Actif'
                },
                {
                    id: 2,
                    structureParent: 'Tech Solutions SARL',
                    role: 'Référent Technique',
                    dateAjout: '2023-11-20',
                    statut: 'Actif'
                },
                {
                    id: 3,
                    structureParent: 'Global Services',
                    role: 'Contact Secondaire',
                    dateAjout: '2024-02-10',
                    statut: 'Inactif'
                }
            ];

            // Filtrer pour ne montrer que celles où le client apparaît
            setAffiliations(mockAffiliations);
        } catch (error) {
            console.error('Erreur chargement affiliations:', error);
            setAffiliations([]);
        } finally {
            setLoading(false);
        }
    };

    const getStatutColor = (statut) => {
        return statut === 'Actif' ? 'success' : 'default';
    };

    if (!client) return null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                }
            }}
        >
            <DialogTitle sx={{
                position: 'relative',
                pb: 1,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <AccountTreeIcon color="secondary" />
                PERSONAE - {client.structure ? client.nom : `${client.prenom} ${client.nom}`}
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    Structures où ce client apparaît comme contact ou référent
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : affiliations.length === 0 ? (
                    <Box sx={{ textAlign: 'center', p: 4 }}>
                        <Typography variant="body1" color="text.secondary">
                            Aucune affiliation trouvée pour ce client
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer component={Paper} variant="outlined">
                        <Table>
                            <TableHead>
                                <TableRow sx={{ backgroundColor: COLORS.gray[50] }}>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Structure Parent</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Rôle</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Date d'ajout</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Statut</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {affiliations.map((affiliation) => (
                                    <TableRow key={affiliation.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="500">
                                                {affiliation.structureParent}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>{affiliation.role}</TableCell>
                                        <TableCell>{new Date(affiliation.dateAjout).toLocaleDateString('fr-FR')}</TableCell>
                                        <TableCell>
                                            <Chip
                                                label={affiliation.statut}
                                                color={getStatutColor(affiliation.statut)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell align="center">
                                            <IconButton
                                                size="small"
                                                color="primary"
                                                onClick={() => {
                                                    // TODO: Navigation vers la structure
                                                    console.log('Voir structure:', affiliation.id);
                                                }}
                                            >
                                                <VisibilityIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                color="secondary"
                                                onClick={() => {
                                                    // TODO: Modifier affiliation
                                                    console.log('Modifier affiliation:', affiliation.id);
                                                }}
                                            >
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={onClose} variant="contained" color="secondary">
                    Fermer
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PersonaeDialog;
